import {
  useCallback,
  useMemo,
  useState,
  type MouseEvent,
  type ReactNode,
} from 'react';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import {
  Box,
  Button,
  Chip,
  ListItemText,
  Menu,
  MenuItem,
  MenuList,
  Typography,
  type TypographyProps,
} from '@mui/material';
import { createTheme, ThemeProvider, useTheme } from '@mui/material/styles';
import { useActive, useCommands, useCurrentSelection } from '@remirror/react';

import { generateCustomHeadingStyles } from '../../theme-helpers';

const HEADING_VARIANTS = [1, 2, 3] as const;

const CustomHeadingsWrapper = ({ children }: { children: ReactNode }) => {
  const appTheme = useTheme();

  const customHeadingsTheme = createTheme({
    ...appTheme,
    typography: {
      ...appTheme.typography,
      ...generateCustomHeadingStyles(appTheme),
    },
  });

  return <ThemeProvider theme={customHeadingsTheme}>{children}</ThemeProvider>;
};

const HeadingButton = ({
  level,
  onClick,
  isActive,
}: {
  level: number;
  onClick: (level: number) => void;
  isActive: boolean;
}) => {
  const isParagraph = level === 0;
  const buttonText = isParagraph ? 'Texto simples' : `Título ${level}`;
  const buttonVariant = isParagraph ? 'body2' : `h${level}`;

  return (
    <MenuItem selected={isActive} value={level} onClick={() => onClick(level)}>
      <ListItemText disableTypography>
        <Typography variant={buttonVariant as TypographyProps['variant']}>
          {buttonText}
        </Typography>
      </ListItemText>
      <Chip label={`⌘${level}`} sx={{ ml: 3 }} size="small" />
    </MenuItem>
  );
};

export const HeadingMenu = () => {
  const { focus, setBlockType } = useCommands();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const active = useActive();
  useCurrentSelection();

  const open = Boolean(anchorEl);

  const activeHeading = useMemo(() => {
    const customHeadingActive = active.customHeading?.();
    if (!customHeadingActive) return 0;

    for (const level of HEADING_VARIANTS) {
      if (active.customHeading?.({ level })) {
        return level;
      }
    }
    return 0;
  }, [active]);

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleClick = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleSelect = useCallback(
    (level: number) => {
      if (level === 0) {
        setBlockType({ type: 'paragraph' });
      } else {
        setBlockType({ type: 'customHeading', attrs: { level } });
      }
      focus();
      handleClose();
    },
    [setBlockType, focus, handleClose]
  );

  const selectedText = activeHeading
    ? `Título ${activeHeading}`
    : 'Texto simples';

  return (
    <Box>
      <Button
        disableElevation
        endIcon={<KeyboardArrowDownIcon />}
        onClick={handleClick}
        size="medium"
        variant="outlined"
        color="inherit"
        sx={{
          borderColor: 'neutral.500',
          fontWeight: 500,
          justifyContent: 'space-between',
          width: 150,
        }}
      >
        {selectedText}
      </Button>
      <CustomHeadingsWrapper>
        <Menu
          anchorEl={anchorEl}
          disablePortal
          open={open}
          onClose={handleClose}
        >
          <MenuList>
            <HeadingButton
              level={0}
              onClick={handleSelect}
              isActive={activeHeading === 0}
            />
            {HEADING_VARIANTS.map((level) => (
              <HeadingButton
                key={level}
                level={level}
                onClick={handleSelect}
                isActive={activeHeading === level}
              />
            ))}
          </MenuList>
        </Menu>
      </CustomHeadingsWrapper>
    </Box>
  );
};
